import {
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  UseQueryResult,
} from '@taxdown/zeno-sama-query';
import { AxiosError } from 'axios';
import { getErrorDetail } from 'Services/errors';
import Notify, { AlertTypes } from 'Services/notification';
import RecordsService from 'Services/records';
import CheckoutService from 'Services/checkout';
import { PueblaPreviousYearsResponse } from 'Services/sat/types';
import SatService from 'Services/sat';
import { history } from 'Store';
import { useGetQuery } from 'Hooks/useGetQuery';
import { routes } from 'Constants/routes';
import { FRONT_PLANS, PLAN_FLEX_LITE, plansNameMapping } from 'Constants/plans';
import {
  CustomerType,
  FlexInfoResponse,
  InstantRefundType,
  QueryKeys,
  ResponseType,
  ResultType,
  ResultTypes,
  SubmitResultType,
  taxReturnFilingType,
  UserComplexResponse,
} from 'Types/common';

const MINIMUN_AMOUNT_TO_PAY_LATER = 100;
const MAXIMUN_AMOUNT_TO_PAY_LATER = 10000;
const AMOUNT_TO_PRO = 10000;
const MINIMUN_AMOUNT_TO_FED = 2196;
const MAXIMUN_AMOUNT_TO_FED = 10000;

interface paymentLaterProps {
  taxReturnType: string;
  taxReturnResult: number;
  instantRefund: boolean;
  userHaveAA?: boolean;
}

export const resultSelector = (response: ResponseType<ResultType>): ResultType => {
  const { data } = response;
  const isRefundable = data.item.warnings.filter(item => item.id === 'O00074');
  const warnings = data.item.warnings.filter(item => item.id !== 'O00074');
  const userToPro = Math.abs(data.item.taxReturnResult) > AMOUNT_TO_PRO;
  const flexUser =
    Math.abs(data.item.taxReturnResult) > MINIMUN_AMOUNT_TO_PAY_LATER &&
    Math.abs(data.item.taxReturnResult) < MAXIMUN_AMOUNT_TO_PAY_LATER;
  const isUserForFed =
    Math.abs(data.item.taxReturnResult) > MINIMUN_AMOUNT_TO_FED &&
    Math.abs(data.item.taxReturnResult) < MAXIMUN_AMOUNT_TO_FED &&
    data.item.taxReturnFilingType === taxReturnFilingType.complementary;
  const needEfirmaUser =
    data.item.taxReturnFilingType === taxReturnFilingType.complementary &&
    data?.item?.taxReturnType === ResultTypes.toReturn &&
    Math.abs(data.item.taxReturnResult) > MAXIMUN_AMOUNT_TO_PAY_LATER;
  const dataFiltered = {
    mandatory:
      data.item.mandatory && data.item.taxReturnFilingType !== taxReturnFilingType.complementary,
    taxReturnResult: data.item.taxReturnResult,
    taxReturnSubType: data.item.taxReturnSubType,
    taxReturnType: data.item.taxReturnType,
    warnings,
    isRefundable,
    flexUser,
    flexToPro: userToPro,
    taxReturnFilingType: data.item.taxReturnFilingType,
    isUserForFed,
    needEfirmaUser,
  } as ResultType;

  return dataFiltered;
};

export const useFetchGetResult = (): UseQueryResult<ResultType> =>
  useQuery([QueryKeys.FETCH_GET_RESULT], RecordsService.getResultData, {
    select: data => resultSelector(data),
    onError: (err: AxiosError) => {
      if (err.response?.status) {
        Notify.sendNotification(getErrorDetail(err), AlertTypes.error);
        history.push(routes.DASHBOARD);
      }
    },
    refetchOnWindowFocus: false,
    refetchInterval: data => (data ? false : 500),
  });

export const resultMock = {
  taxReturnResult: -1539,
  taxReturnType: 'toPay',
  taxReturnSubType: 'returnLessThan10K',
  mandatory: true,
  warnings: [],
};

export const useGetInfoResultUser = (): ResultType | undefined => {
  const response = useGetQuery<ResponseType<ResultType>>([QueryKeys.FETCH_GET_RESULT]);
  return response?.data?.item;
};

export const useSavePayLater = (
  submitInfo: paymentLaterProps
): UseMutationResult<ResponseType<unknown>, { taxYear: string; type: string }> => {
  return useMutation([QueryKeys.SUBSCRIBE_FLEX_LITE], CheckoutService.subscribeLiteFlex, {
    onSuccess: () => {
      const routeGoTo = `${routes.CHECKOUT_SUCCESS_STEP}?filing=true&plans=${FRONT_PLANS.FLEX}`;
      history.push(routeGoTo, {
        taxReturnType: submitInfo?.taxReturnType,
        taxReturnResult: submitInfo?.taxReturnResult,
        plan: { name: FRONT_PLANS.FLEX },
      });
    },
  });
};

export const useSaveInstantRefund = (): UseMutationResult<
  ResponseType<InstantRefundType>,
  unknown,
  unknown,
  unknown
> => {
  return useMutation([QueryKeys.SAVE_INSTANT_REFUND], RecordsService.saveInstantRefund, {
    cacheTime: Infinity,
  });
};

export const useFetchGetUserComplex = (): UseQueryResult<UserComplexResponse> =>
  useQuery([QueryKeys.GET_USER_COMPLEX], CheckoutService.getUserComplex, {
    select: data => {
      const response = data.data;
      return response.item;
    },
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

export const useFetchGetUserProfile = (): UserComplexResponse | undefined => {
  const response = useGetQuery<ResponseType<UserComplexResponse>>([QueryKeys.GET_USER_COMPLEX]);
  const data = response?.data?.item;
  return data;
};

export const useFetchRequestInstantRefund = (): UseQueryResult<boolean> => {
  return useQuery(
    [QueryKeys.GET_INSTANT_REFUND_REQUEST],
    () => RecordsService.getInstantRefundRequest(),
    {
      select: data => {
        const response = data?.data;
        if (response?.item?.status) {
          return true;
        }
        return false;
      },
      refetchOnWindowFocus: false,
      cacheTime: 0,
    }
  );
};

export const useGetPlanFlexInfo = (): UseQueryResult<FlexInfoResponse> =>
  useQuery([QueryKeys.GET_PLAN_FLEX_INFO], () => CheckoutService.getPlanFlexInfo(), {
    select: data => {
      const response = data?.data;
      return response.item;
    },
    onError: () => {
      history.push(routes.DASHBOARD);
    },
    refetchOnWindowFocus: false,
  });

export const useSubmitDeclaration = (): UseMutationResult<ResponseType<SubmitResultType>> =>
  useMutation([QueryKeys.SUBMIT_ANUAL_DECLARATION], RecordsService.setSubmit);

export const useGetResultExtraInformation = (): [
  UseQueryResult<PueblaPreviousYearsResponse>,
  UseQueryResult<CustomerType>,
] =>
  useQueries({
    queries: [
      {
        queryKey: [QueryKeys.GET_PREVIOUS_YEAR_RESULT],
        queryFn: SatService.getPueblaPreviousYears,
        select: data => {
          const response = data.data;
          return response.item;
        },
        refetchOnWindowFocus: false,
      },
      {
        queryKey: [QueryKeys.FETCH_GET_CUSOMERS],
        queryFn: CheckoutService.getCustomer,
        select: data => {
          const customer = data.data;
          return customer.item;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
      },
    ],
  });

export const useGetUserPlan = (): FRONT_PLANS => {
  const data = useGetQuery<ResponseType<CustomerType>>([QueryKeys.FETCH_GET_CUSOMERS]);
  const customerInfo = data?.data?.item;
  const plan = customerInfo?.activePlan
    ? customerInfo.activePlan.name === PLAN_FLEX_LITE
      ? FRONT_PLANS.FLEX_LITE
      : plansNameMapping(customerInfo.activePlan.name)
    : FRONT_PLANS.BASIC;
  return plan;
};
